import React from "react";
import { Badge, Button, IndexTable } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { TableSkeleton } from "../../../components/skeletonLoader";
import {
  formatCount,
  formatDateTime,
  formatDuration,
  formatRelativeTime,
  merchantScreenLabel,
  readableStatus,
  statusTone,
  storeBilling,
  storeDomain,
  storeHealth,
  storeId,
  storeName,
  storePipeline,
} from "../../support/supportFormatters";
import "./style.css";

const StatusBadge = ({ status }) => (
  <Badge status={statusTone(status)}>{readableStatus(status)}</Badge>
);

const LabeledStatus = ({ label, status }) => (
  <span className="support-store-table__labeled-status">
    <span>{label}</span>
    <Badge status={statusTone(status)}>{readableStatus(status)}</Badge>
  </span>
);

const connectionStatus = (store, key) =>
  store?.connections?.[key]?.status ||
  store?.[`${key}Status`] ||
  (store?.[`${key}Connected`] === true
    ? "CONNECTED"
    : store?.[`${key}Connected`] === false
    ? "DISCONNECTED"
    : "UNKNOWN");

const StoreTable = ({ stores = [], loading = false }) => {
  const navigate = useNavigate();

  if (loading) return <div className="support-store-table__loading"><TableSkeleton /></div>;

  const rows = stores.map((store, index) => {
    const id = storeId(store);
    const pipeline = storePipeline(store);
    const queued = pipeline.queued ?? store?.queued;
    const processing = pipeline.processing ?? store?.processing;
    const attention =
      pipeline.manualReview ?? pipeline.attention ?? store?.manualReview ?? store?.attention;
    const retryWait = pipeline.retryWait ?? store?.retryWait;
    const blocked = pipeline.blocked ?? store?.blocked;
    const oldest = pipeline.oldestQueuedAt || store?.oldestQueuedAt;
    const lastSuccess =
      store?.lastSuccessfulSyncAt || store?.lastSuccessfulFulfillmentAt || store?.lastSyncAt;
    const installedAt = store?.installedAt || store?.creationDate || store?.createdAt;
    const billing = store?.billing || {};
    const onboarding = store?.onboarding || {};
    const disputes = store?.disputes || {};
    const paypal = store?.connections?.paypal || {};
    const merchantScreen = merchantScreenLabel({
      ...onboarding,
      merchantScreen:
        onboarding.merchantScreen ||
        (onboarding.completedAt
          ? "DASHBOARD"
          : onboarding.startedAt
          ? "ONBOARDING"
          : "LEGACY_DASHBOARD"),
    });

    return (
      <IndexTable.Row id={String(id ?? index)} key={String(id ?? index)} position={index}>
        <IndexTable.Cell>
          <button
            type="button"
            className="support-store-table__identity"
            onClick={() => id !== null && navigate(`/store/${encodeURIComponent(id)}`)}
            disabled={id === null}
          >
            <strong>{storeName(store)}</strong>
            <span>{storeDomain(store)}</span>
            <span>
              Shopify ID {store?.externalId || store?.shopifyId || id || "unknown"}
              {store?.owner || store?.shopOwner ? ` · ${store.owner || store.shopOwner}` : ""}
            </span>
          </button>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge status={storeHealth(store)} />
          {store?.issueSummary || store?.summary ? (
            <span className="support-store-table__subtext support-store-table__issue">
              {store.issueSummary || store.summary}
            </span>
          ) : null}
          <span className="support-store-table__subtext">
            Merchant sees {merchantScreen}
          </span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <div className="support-store-table__badges">
            <LabeledStatus label="Shopify" status={connectionStatus(store, "shopify")} />
            <LabeledStatus label="PayPal" status={paypal.connectionHealth || connectionStatus(store, "paypal")} />
            <span className="support-store-table__subtext">
              {formatCount(paypal.activeAccounts)} usable · {formatCount(paypal.unhealthyAccounts)} unhealthy
            </span>
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge status={storeBilling(store)} />
          <span className="support-store-table__subtext">
            {billing.tier || store?.billingTier || store?.currentTier || "Usage tier unknown"}
          </span>
          {billing.trialEndsAt || store?.trialEndsAt ? (
            <span className="support-store-table__subtext">
              Trial ends {formatDateTime(billing.trialEndsAt || store.trialEndsAt)}
            </span>
          ) : null}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <strong>{formatCount(disputes.open)}</strong> open
          <span className="support-store-table__subtext">
            {formatCount(disputes.attention)} attention · {formatCount(disputes.overdue)} overdue
          </span>
          <span className="support-store-table__subtext">
            Sync {readableStatus(disputes.syncStatus)} · {formatRelativeTime(disputes.lastSyncedAt)}
          </span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <strong>{formatCount(queued)}</strong> queued
          <span className="support-store-table__subtext">
            {formatCount(processing)} processing · {formatCount(attention)} manual review
          </span>
          <span className="support-store-table__subtext">
            {formatCount(retryWait)} retry wait · {formatCount(blocked)} terminal
          </span>
          <span className="support-store-table__subtext">Oldest {formatRelativeTime(oldest)}</span>
          <span className="support-store-table__subtext">P95 {formatDuration(pipeline.p95SyncMs)}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {formatRelativeTime(lastSuccess)}
          <span className="support-store-table__subtext">{formatDateTime(lastSuccess)}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {readableStatus(onboarding.status, "Unknown")}
          {onboarding.currentStep ? (
            <span className="support-store-table__subtext">Step {onboarding.currentStep} of 3</span>
          ) : null}
          <span className="support-store-table__subtext">Installed {formatDateTime(installedAt)}</span>
          <span className="support-store-table__subtext">Activity {formatRelativeTime(store?.lastActivityAt)}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Button
            onClick={() => id !== null && navigate(`/store/${encodeURIComponent(id)}`)}
            disabled={id === null}
          >
            Open
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <>
      <IndexTable
        resourceName={{ singular: "store", plural: "stores" }}
        itemCount={stores.length}
        selectable={false}
        headings={[
          { title: "Store" },
          { title: "Health" },
          { title: "Connections" },
          { title: "Usage billing" },
          { title: "Disputes" },
          { title: "Live sync" },
          { title: "Last success" },
          { title: "Lifecycle" },
          { title: "" },
        ]}
      >
        {rows}
      </IndexTable>
      {stores.length === 0 ? (
        <div className="support-store-table__empty" role="status">
          <h3>No stores match these filters</h3>
          <p>Clear a filter or search for another store identity.</p>
        </div>
      ) : null}
    </>
  );
};

export default StoreTable;
